import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { brandMilestones, contactInfo } from '@/mocks/sports';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('sending');
    setFormError('');

    const formEl = e.currentTarget;
    const formData = new FormData(formEl);

    const honeypotValue = (formData.get('company_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9slf3d8o1vn02ekuhe0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'company_alt')
            .map(([k, v]) => [k, v.toString()])
        ).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        formEl.reset();
      } else {
        const serverMsg = (parsed as Record<string, unknown> | null)?.meta && (parsed as Record<string, unknown>).meta?.message
          ? String((parsed as Record<string, unknown>).meta?.message)
          : (parsed as Record<string, unknown> | null)?.message
            ? String((parsed as Record<string, unknown>).message)
            : responseText;
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' && serverMsg ? serverMsg : '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '米乐体育',
    url: 'https://www.milesports.com',
    logo: 'https://www.milesports.com/logo.png',
    description: '米乐体育是专业体育赛事平台，汇聚全球顶级赛事直播、体育竞技、数据分析与互动娱乐。',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-6666',
      contactType: 'customer service',
      contactOption: 'TollFree',
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '建国路88号SOHO现代城A座18层',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
    foundingDate: '2018',
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 米乐体育 | 品牌介绍、信誉与联系方式"
        description="米乐体育创立于2018年，是专业体育赛事直播平台。了解米乐体育品牌故事、发展历程、行业信誉及联系方式。汇聚全球顶级赛事直播，数据驱动体育娱乐新体验。"
        keywords="米乐体育,米乐体育官网,关于米乐体育,米乐体育品牌,米乐体育联系方式,体育赛事平台"
        canonicalUrl="/about"
        ogType="website"
        jsonLd={orgSchema}
      />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            <div className="pb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">关于米乐体育</h1>
              <p className="text-sm text-foreground-500 max-w-2xl">了解我们的品牌故事、发展历程与核心价值</p>
            </div>
          </div>
        </div>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                  让体育更精彩
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p>
                    <strong>米乐体育</strong>创立于2018年，总部位于北京，是一家专注于体育赛事直播与体育娱乐的综合服务平台。自创立以来，我们始终秉承<strong>"让体育更精彩"</strong>的使命，致力于为全球体育爱好者提供最优质的赛事直播和数据服务。
                  </p>
                  <p>
                    米乐体育平台汇聚了包括<strong>足球、篮球、电竞、网球、综合体育</strong>在内的超过200项全球顶级赛事版权，年直播场次超过50,000场。我们采用先进的流媒体技术和AI数据引擎，确保每一场赛事都能以最高清、最流畅的画质呈现给用户。
                  </p>
                  <p>
                    经过多年的发展，米乐体育已经成为拥有超过<strong>5,800万全球注册用户</strong>的行业领军平台。我们不仅提供赛事直播，还深度整合了数据分析、社区互动、体育资讯等多元化服务，打造一站式的体育娱乐生态。
                  </p>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20sports%20media%20office%20with%20multiple%20screens%20showing%20live%20sports%20events%2C%20professional%20broadcast%20studio%20setup%2C%20warm%20lighting%2C%20contemporary%20design%2C%20red%20and%20gold%20accent%20details%2C%20clean%20corporate%20aesthetic&width=800&height=600&seq=about-brand-sports&orientation=landscape"
                  alt="米乐体育品牌 - 专业体育赛事直播平台"
                  title="米乐体育 - 专业体育赛事直播平台"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">品牌信誉</h2>
              <p className="text-sm text-foreground-500 mt-2">信赖源于品质，荣誉见证实力</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {[
                { icon: 'ri-shield-check-line', title: '安全可靠', desc: '多重加密技术保障用户数据安全，通过ISO 27001信息安全认证' },
                { icon: 'ri-award-line', title: '行业认证', desc: '获得国家体育总局认可，中国互联网体育平台优秀企业' },
                { icon: 'ri-customer-service-2-line', title: '优质服务', desc: '7×24小时在线客服，平均响应时间不超过30秒' },
                { icon: 'ri-verified-badge-line', title: '正版授权', desc: '所有赛事内容均获得官方正版授权，合法合规运营' },
              ].map((item) => (
                <div key={item.title} className="bg-background-50 rounded-lg p-6 text-center border border-background-200/70">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-accent-50 flex items-center justify-center text-accent-500">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">发展历程</h2>
              <p className="text-sm text-foreground-500 mt-2">从创业到行业领军，每一步都坚实有力</p>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 -translate-x-1/2 hidden md:block"></div>

              <div className="hidden md:block space-y-8">
                {brandMilestones.map((item, index) => (
                  <div
                    key={item.year}
                    className={`relative flex items-center gap-8 ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? 'text-right' : 'text-left'}`}>
                      <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                        <span className="font-heading text-xl font-bold text-primary-500">{item.year}</span>
                        <h3 className="font-semibold text-foreground-900 mt-1">{item.title}</h3>
                        <p className="text-xs text-foreground-500 mt-1">{item.description}</p>
                      </div>
                    </div>
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-500 border-4 border-background-50 flex items-center justify-center z-10">
                      <span className="w-2 h-2 bg-background-50 rounded-full"></span>
                    </div>
                    <div className="flex-1"></div>
                  </div>
                ))}
              </div>

              <div className="md:hidden space-y-4">
                {brandMilestones.map((item) => (
                  <div key={item.year} className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-500 border-4 border-background-50 flex items-center justify-center">
                      <span className="w-2 h-2 bg-background-50 rounded-full"></span>
                    </div>
                    <div className="bg-background-100 rounded-lg p-4 flex-1 border border-background-200/70">
                      <span className="font-heading text-lg font-bold text-primary-500">{item.year}</span>
                      <h3 className="font-semibold text-foreground-900 mt-0.5 text-sm">{item.title}</h3>
                      <p className="text-xs text-foreground-500 mt-1">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">联系我们</h2>
              <p className="text-sm text-foreground-500 mt-2">我们随时为您提供帮助</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: 'ri-phone-line', label: '客服热线', value: contactInfo.phone },
                    { icon: 'ri-mail-line', label: '电子邮箱', value: contactInfo.email },
                    { icon: 'ri-map-pin-line', label: '公司地址', value: contactInfo.address },
                    { icon: 'ri-time-line', label: '工作时间', value: contactInfo.workingHours },
                  ].map((info) => (
                    <div key={info.label} className="bg-background-50 rounded-lg p-5 border border-background-200/70">
                      <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center text-primary-500 mb-3">
                        <i className={`${info.icon} text-lg`}></i>
                      </div>
                      <p className="text-xs text-foreground-500 mb-1">{info.label}</p>
                      <p className="text-sm font-semibold text-foreground-900">{info.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-background-50 rounded-lg p-6 border border-background-200/70">
                <h3 className="font-semibold text-foreground-900 mb-4 text-sm">发送消息</h3>
                <form data-readdy-form="" id="contact-form" onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 focus:outline-none focus:border-primary-500 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-xs font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="antispam-field"
                  />
                  <button
                    type="submit"
                    disabled={formStatus === 'sending'}
                    className="w-full bg-primary-500 text-background-50 py-2.5 rounded-md text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === 'sending' ? '提交中...' : '提交留言'}
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-xs text-green-600 text-center">感谢您的留言，我们会尽快回复！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-xs text-primary-500 text-center">{formError || '提交失败，请重试'}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}